import React, { useState, useEffect, useRef } from 'react';

import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { saveAs } from 'file-saver';
import { useResizeObserver, useCopyToClipboard } from 'usehooks-ts';

import { Table, notification, Drawer, Flex, Tooltip, Modal, Button, Form, Input, Switch, Tag, DatePicker, Popconfirm } from 'antd';
import { ColumnsType } from 'antd/es/table';
import type { GetRef } from 'antd';

import {
    ReloadOutlined,
    HistoryOutlined,
    SwapOutlined,
    PlusOutlined,
    EditOutlined,
    EditFilled,
    FileExcelOutlined,
    LoadingOutlined,
    QuestionCircleOutlined,
    DeleteOutlined,
    DeleteFilled,
    MinusOutlined,
    CopyOutlined,
} from '@ant-design/icons';

import { useAppDispatch, useAppSelector } from '@store/hooks';

import DepositingModal from '@components/balance-details/depositing-modal';
import TransferAccountsModal from '@components/balance-details/transfer-accounts-modal';
import RubleBillModal from '@components/balance-details/ruble-bill-modal';
import BillModal from '@components/balance-details/bill-modal';
import RefundModal from '@components/balance-details/refund-modal';
import IBaseComponentProps from '@components/base-component-props';
import Comment from '@controls/comment/comment';
import Transactions, {
    Comment as TransactionComment,
    TransactionTypeBackgroundColor,
    TransactionTypeColor,
} from '@components/transactions/transactions';

import FormHeader from '@controls/form-header/form-header';
import Bill from '@controls/bill/bill';
import Toolbar from '@controls/toolbar/toolbar';
import Account from '@controls/account';
import Filter from '@controls/filter/filter';

import { exception, info } from '@extensions/notification';
import { delayAction, toFinanceString, dataURItoBlob, getDate } from '@extensions/utils';
import { userLoaded, setFilter } from '@store/actions';

import { ITransactionParams } from '@entities/transaction-params';
import { IUserBalanceLineFilter } from '@entities/user-balance-line-filter';
import { IUserBalanceLine } from '@entities/user-balance-line';
import { IUser } from '@entities/user';
import { IUserAccount } from '@entities/user-account';
import { IBill } from '@entities/bill';
import { IBillFilter } from '@entities/bill-filter';
import { IDeleteBillParams } from '@entities/delete-bill-params';
import { IUserSession } from '@entities/user-session';

import { UserAccountType } from '@enums/user-account-type';
import { Currency, enumSign as currencySign } from '@enums/currency';
import { UserBalanceDetailTypes as UserBalanceDetailType } from '@enums/user-balance-detail-type';
import { TransactionType } from '@enums/transaction-types';
import { hasPermission, Permission } from '@enums/permission';
import { UserBalanceType } from '@enums/user-balance-type';

import { WalletIcon, ServicePayIcon, RefundIcon, RubleCircleIcon } from '@icons/index';

dayjs.extend(utc);

type TableRef = GetRef<typeof Table>;

const filterContext: string = 'UserBalanceDetails';

interface IBalanceDetailsProps extends IBaseComponentProps {
    userId: string;
    userBalanceType: UserBalanceType;
    isViewMode: boolean;
    showExchangeRateInfo: boolean;

    useTagRequired: boolean;
    tag: string | undefined;
}

const BalanceDetailsControl = (props: IBalanceDetailsProps) => {
    const { RangePicker } = DatePicker;

    const { userId, userBalanceType, useTagRequired, tag, isViewMode, serverFetch } = props;

    const apiPath = userBalanceType == UserBalanceType.Regular ? 'userregularbalances' : 'userwhitebalances';

    const initFilter: IUserBalanceLineFilter = {};

    const { TextArea } = Input;
    const [deletelForm] = Form.useForm();

    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const [, copy] = useCopyToClipboard();

    const handleCopy = (text: string) => () => {
        copy(text).then(() => {
            info(api, `"${text}" скопирован`);
        });
    };

    const tableRef = useRef<TableRef>(null);
    const [modal, modalContextHolder] = Modal.useModal();

    const d = useAppDispatch();

    const [api, notificationContextHolder] = notification.useNotification();

    const filter = useAppSelector<IUserBalanceLineFilter>((s) => s.filters[filterContext]);

    const [showFilter, setShowFilter] = useState<boolean>(true);

    const [currentBalance, setCurrentBalance] = useState<number>(0);
    const [lines, setLines] = useState<Array<IUserBalanceLine>>([]);
    const [currentLine, setCurrentLine] = useState<IUserBalanceLine>();
    const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const [lastSelectedKey, setLastSelectedKey] = useState<React.Key>();

    const [openDepositingModal, setOpenDepositingModal] = useState<boolean>(false);
    const [openTransferAccountsModal, setOpenTransferAccountsModal] = useState<boolean>(false);
    const [openBillModal, setOpenBillModal] = useState<boolean>(false);
    const [openTransactions, setOpenTransactions] = useState<boolean>(false);
    const [openRefundModal, setOpenRefundModal] = useState<boolean>(false);

    const [userAccounts, setUserAccounts] = useState<Array<IUserAccount>>([]);
    const [cargoAccount, setCargoAccount] = useState<IUserAccount>();

    const [user, setUser] = useState<IUser>();

    const [openBill, setOpenBill] = useState<boolean>(false);
    const [bills, setBills] = useState<Array<IBill>>([]);

    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [openRubleModal, setOpenRubleModal] = useState<boolean>(false);
    const [useTag, setUseTag] = useState<boolean>(false);

    useEffect(() => {
        setRefreshRequired(true);
    }, [userBalanceType]);

    useEffect(() => {
        let cleanup = false;

        if (!refreshRequired) return;

        setSelectedIds([]);
        setCurrentLine(undefined);
        setLines([]);

        const fetchData = async () => {
            setLoading(true);

            const promises = [
                await serverFetch(`${apiPath}/${userId}/lines`, { method: 'GET', queryParams: filter })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения балансов', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch(`users/${userId}`, { method: 'GET' })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения пользователя', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch(`users/${userId}/accounts`, { method: 'GET' })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения счетов пользователя', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch(`${apiPath}/${userId}/current`, { method: 'GET' })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения текущего баланса пользователя', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setLines(result[0][0]);
                setUser(result[0][1]);

                let accounts: Array<IUserAccount> = result[0][2];
                accounts = accounts.filter((a) => !a.userBalanceType || a.userBalanceType == userBalanceType);

                setUserAccounts(accounts);
                setCargoAccount(accounts.find((a) => a.type === UserAccountType.Transport));

                setCurrentBalance(result[0][3]);

                setLoading(false);
                setRefreshRequired(false);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, [refreshRequired]);

    useEffect(() => {
        d(setFilter({ ...filter, userId: userId, useTagRequired: useTagRequired }, filterContext));
    }, [userId, useTagRequired]);

    useEffect(() => {
        if (!loading && lastSelectedKey) {
            tableRef.current?.scrollTo({ key: lastSelectedKey });
            setLastSelectedKey(undefined);
        }
    }, [loading]);

    useEffect(() => {
        if (!filter) {
            d(setFilter({ ...initFilter, userId: userId, useTagRequired: useTagRequired }, filterContext));
            return;
        }

        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    useEffect(() => {
        if (bills && bills.length > 0) {
            setOpenBill(true);
        }
    }, [bills]);

    const onLoadBills = (billIds: Array<string>) => {
        setBills([]);

        const billFilter: IBillFilter = {
            ids: billIds,
        };

        serverFetch('bills', { method: 'GET', queryParams: billFilter })
            .then((data: any) => {
                setBills(data);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка получения счетов', ex, () => d(userLoaded(undefined)));
            });
    };

    const onSelectChange = (selectedRowKeys: React.Key[], selectedRows: IUserBalanceLine[]) => {
        const rowKeys: React.Key[] = [];
        selectedRows.map((r) => {
            if (r.type != UserBalanceDetailType.Transaction) rowKeys.push(r.id);
        });

        setSelectedIds(rowKeys);

        setLastSelectedKey(rowKeys[0]);

        if (rowKeys.length == 1) {
            const entity = lines.find((o) => o.id == rowKeys[0]);
            setCurrentLine(entity);
        } else {
            setCurrentLine(undefined);
        }
    };

    const onSaveBill = (entity: IBill) => {
        if (!entity) return;
        entity.userBalanceType = userBalanceType;

        serverFetch(`bills`, { method: entity.id ? 'PUT' : 'POST', bodyData: entity })
            .then(() => {
                setOpenBillModal(false);
                setRefreshRequired(true);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка выставления счета', ex, () => d(userLoaded(undefined)));
            });
    };

    const onExport = () => {
        serverFetch(`${apiPath}/export`, { method: 'GET', queryParams: filter })
            .then((data: any) => {
                const fileBody = `data:${data.contentType};base64,${data.fileContents}`;
                saveAs(dataURItoBlob(fileBody), data.fileDownloadName);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка выгрузки счетов', ex, () => d(userLoaded(undefined)));
            });
    };

    const onDebiting = () => {
        if (!userId) return;

        const entity: ITransactionParams = {
            userId: userId,
            lineIds: selectedIds as Array<string>,
            userBalanceType: userBalanceType,
        };

        serverFetch(`transactions/debiting`, { method: 'POST', bodyData: entity })
            .then(() => {
                setRefreshRequired(true);
            })
            .catch((ex: any) => {
                setRefreshRequired(true);
                exception(api, 'Ошибка оплаты счетов', ex, () => d(userLoaded(undefined)));
            });
    };

    const onDelete = (data: IDeleteBillParams) => {
        if (!currentLine) return;

        data.ids = [currentLine.referenceId];

        serverFetch(`bills`, { method: 'DELETE', bodyData: data })
            .then(() => {
                deletelForm.resetFields();
                setOpenDeleteModal(false);
                setRefreshRequired(true);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка удаления счета', ex, () => d(userLoaded(undefined)));
            });
    };

    const onSaveComment = (id: string, comment: string | undefined, groupId?: string) => {
        var data = {
            id: id,
            groupId: groupId,
            comment: comment,
        };

        serverFetch(`${apiPath}/comment`, { method: 'POST', bodyData: data })
            .then(() => {
                setRefreshRequired(true);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка сохранения комментария', ex, () => d(userLoaded(undefined)));
            });
    };

    const onDeleteGroup = (groupId: string) => {
        serverFetch(`${apiPath}/groups/${groupId}`, { method: 'DELETE' })
            .then(() => {
                setRefreshRequired(true);
            })
            .catch((ex: any) => {
                setLoading(false);
                exception(api, 'Ошибка возврата коробки с баланса', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderFilter = () => {
        return (
            <div ref={filterRef}>
                <Filter
                    display={showFilter}
                    items={[
                        <Input
                            style={{ width: 150 }}
                            key='number'
                            placeholder='Машина'
                            value={filter?.truckNumber}
                            onChange={(data) => {
                                d(setFilter({ ...filter, truckNumber: data.target.value }, filterContext));
                            }}
                        />,
                        <Input
                            style={{ width: 150 }}
                            key='awbNumber'
                            placeholder='AWB'
                            value={filter?.awbNumber}
                            onChange={(data) => {
                                d(setFilter({ ...filter, awbNumber: data.target.value }, filterContext));
                            }}
                        />,
                        <RangePicker
                            style={{ width: 220 }}
                            allowEmpty={[true, true]}
                            key='dateOn'
                            format='DD.MM.YYYY'
                            placeholder={['Дата с', 'до']}
                            cellRender={(current) => {
                                return <div className='ant-picker-cell-inner'>{(current as Dayjs).date()}</div>;
                            }}
                            value={[filter?.dateFrom ? dayjs(filter?.dateFrom) : null, filter?.dateTo ? dayjs(filter?.dateTo) : null]}
                            onChange={(value) => {
                                if (!value) {
                                    d(
                                        setFilter(
                                            {
                                                ...filter,
                                                dateFrom: undefined,
                                                dateTo: undefined,
                                            },
                                            filterContext,
                                        ),
                                    );

                                    return;
                                }

                                d(
                                    setFilter(
                                        {
                                            ...filter,
                                            dateFrom: value[0] ? dayjs(value[0]).utc(true).startOf('day').toString() : undefined,
                                            dateTo: value[1] ? dayjs(value[1]).utc(true).endOf('day').toString() : undefined,
                                        },
                                        filterContext,
                                    ),
                                );
                            }}
                        />,
                        <Form.Item key='tag' label={tag} style={{ margin: 0, display: useTagRequired ? 'block' : 'none' }}>
                            <Switch
                                checked={useTag}
                                onChange={(value: boolean) => {
                                    setUseTag(value);

                                    if (value) {
                                        d(setFilter({ ...filter, tag: tag }, filterContext));
                                    } else {
                                        d(setFilter({ ...filter, tag: undefined }, filterContext));
                                    }
                                }}
                            />
                        </Form.Item>,
                    ]}
                    onClear={() => {
                        d(setFilter({ ...initFilter, userId: userId, useTagRequired: useTagRequired }, filterContext));
                        setUseTag(false);
                    }}
                />
            </div>
        );
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    {
                        label: 'Добавить счет',
                        key: 'addBill',
                        disabled: loading,
                        icon: <PlusOutlined />,
                        hidden: isViewMode,
                        onClick: () => {
                            setOpenBillModal(true);
                        },
                    },
                    {
                        label: 'Изменить счет',
                        key: 'editBill',
                        disabled:
                            loading ||
                            !currentLine ||
                            currentLine.type == UserBalanceDetailType.Truck ||
                            currentLine.type == UserBalanceDetailType.Transaction,
                        icon: <EditOutlined />,
                        hidden: isViewMode,
                        onClick: () => {
                            setOpenBillModal(true);
                        },
                    },
                    {
                        label: 'Удалить',
                        key: 'delete',
                        disabled:
                            loading ||
                            !currentLine ||
                            currentLine.type == UserBalanceDetailType.Truck ||
                            currentLine.type == UserBalanceDetailType.Transaction,
                        icon: <DeleteOutlined />,
                        hidden: isViewMode,
                        onClick: () => {
                            modal.confirm({
                                title: `Удалить выбранный счет?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'Удалить',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    setOpenDeleteModal(true);
                                },
                            });
                        },
                    },
                    {
                        label: 'Выставить счет в рублях',
                        key: 'rubleBill',
                        type: 'primary',
                        icon: <RubleCircleIcon />,
                        hidden: isViewMode,
                        disabled: loading || selectedIds.length <= 0,
                        onClick: () => {
                            setCurrentLine(undefined);
                            setOpenRubleModal(true);
                        },
                    },
                    {
                        isDivider: true,
                        hidden: isViewMode,
                    },

                    {
                        label: 'Внести денежные средства на счет',
                        key: 'wallet',
                        icon: <WalletIcon />,
                        hidden: isViewMode,
                        onClick: () => {
                            setOpenDepositingModal(true);
                        },
                    },
                    {
                        label: 'Перевод между счетами',
                        key: 'transfer',
                        icon: <SwapOutlined />,
                        hidden: isViewMode,
                        onClick: () => {
                            setOpenTransferAccountsModal(true);
                        },
                    },
                    {
                        label: 'Возврат денежных средств',
                        key: 'refund',
                        icon: <RefundIcon />,
                        hidden: isViewMode,
                        onClick: () => {
                            setOpenRefundModal(true);
                        },
                    },
                    {
                        label: 'Оплатить счета',
                        disabled: loading,
                        key: 'pay',
                        type: 'primary',
                        icon: <ServicePayIcon />,
                        hidden: isViewMode,
                        onClick: () => {
                            let total = 0;

                            modal.confirm({
                                title:
                                    selectedIds.length <= 0
                                        ? `Оплатить счета начиная с поздней даты выставления?`
                                        : 'Оплатить выбранные счета?',
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'ОК',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onDebiting();
                                },
                            });
                        },
                    },
                    {
                        isDivider: true,
                        hidden: isViewMode,
                    },
                    {
                        label: 'Выгрузить в Excel',
                        key: 'exportToExcel',
                        disabled: loading,
                        icon: <FileExcelOutlined />,
                        onClick: () => onExport(),
                    },
                    {
                        label: 'История операций',
                        key: 'history',
                        icon: <HistoryOutlined />,
                        hidden: isViewMode,
                        onClick: () => {
                            setOpenTransactions(true);
                        },
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const renderTable = () => {
        const columns: ColumnsType<IUserBalanceLine> = [
            {
                title: '#',
                width: 80,
                align: 'center',
                dataIndex: 'sequence',
                hidden: !hasPermission(userSession.permissions, Permission.FullAccess),
            },
            {
                title: 'Дата',
                width: 100,
                align: 'center',
                onCell: (record) => ({
                    style: {
                        background:
                            record.type == UserBalanceDetailType.Transaction
                                ? TransactionTypeBackgroundColor(record.transactionType)
                                : record.type == UserBalanceDetailType.ExtraBill
                                  ? '#fff2cc'
                                  : '',
                    },
                }),
                render: (_, record) => {
                    return getDate(record.dateOn);
                },
            },

            {
                title: 'Машина',
                width: 200,
                onCell: (record) => ({
                    colSpan: record.type == UserBalanceDetailType.Transaction ? 10 : record.type == UserBalanceDetailType.ExtraBill ? 7 : 1,
                    style: {
                        background:
                            record.type == UserBalanceDetailType.Transaction
                                ? TransactionTypeBackgroundColor(record.transactionType)
                                : record.type == UserBalanceDetailType.ExtraBill
                                  ? '#fff2cc'
                                  : '',
                    },
                }),
                render: (_, record) => {
                    if (record.type == UserBalanceDetailType.Transaction) {
                        return TransactionComment(TransactionType.Transfer, undefined, undefined, record.extraComment, undefined, tableRef);
                    }

                    if (record.type == UserBalanceDetailType.ExtraBill) {
                        return (
                            <Button
                                variant='link'
                                color='blue'
                                style={{ fontWeight: 400, height: 'fit-content', padding: 0 }}
                                onClick={() => onLoadBills(JSON.parse(record.jsonBody || ''))}
                            >
                                {record.extraComment}
                            </Button>
                        );
                    }

                    return (
                        <Flex className='copy-container' align='center' justify='space-between' style={{ width: '100%' }}>
                            <Button
                                variant='link'
                                color='blue'
                                style={{ fontWeight: 400, height: 'fit-content', padding: 0 }}
                                onClick={() => onLoadBills(JSON.parse(record.jsonBody || ''))}
                            >
                                {record.truckNumber}
                            </Button>
                            {record.truckNumber && (
                                <Tooltip title='Копировать' color='cyan'>
                                    <Button
                                        style={{ height: 'fit-content', padding: 0 }}
                                        className='hide-copy'
                                        size='small'
                                        variant='link'
                                        color='cyan'
                                        icon={<CopyOutlined />}
                                        onClick={handleCopy(record.truckNumber)}
                                    />
                                </Tooltip>
                            )}
                        </Flex>
                    );
                },
            },
            {
                title: 'Объем',
                width: 120,
                align: 'center',
                dataIndex: 'volumeWeight',
                onCell: (record) => ({
                    colSpan: record.type == UserBalanceDetailType.Transaction || record.type == UserBalanceDetailType.ExtraBill ? 0 : 1,
                }),
            },
            {
                title: 'Кол-во / шт',
                width: 100,
                align: 'center',
                dataIndex: 'qty',
                onCell: (record) => ({
                    colSpan: record.type == UserBalanceDetailType.Transaction || record.type == UserBalanceDetailType.ExtraBill ? 0 : 1,
                }),
            },
            {
                title: 'Тариф / € ',
                width: 200,
                align: 'center',
                dataIndex: 'tariff',
                onCell: (record) => ({
                    colSpan: record.type == UserBalanceDetailType.Transaction || record.type == UserBalanceDetailType.ExtraBill ? 0 : 1,
                }),
            },
            {
                title: 'К оплате / €',
                width: 200,
                align: 'center',
                onCell: (record) => ({
                    colSpan: record.type == UserBalanceDetailType.Transaction ? 0 : 1,
                    style: {
                        background: '#FFEE96',
                        fontWeight: 600,
                    },
                }),
                render: (_: any, record) => {
                    if (!record.amount) return;

                    let statusTag: any = (
                        <Tooltip color='var(--main-red)' title='Не оплачен'>
                            <Tag color='red'>НЕ ОПЛАЧЕН</Tag>
                        </Tooltip>
                    );

                    if (record.paidAmount) {
                        if (record.paidAmount == record.amount) {
                            statusTag = (
                                <Tooltip color='var(--main-green)' title='Оплачен полность'>
                                    <Tag color='var(--main-green)' variant='solid'>
                                        ОПЛАЧЕН
                                    </Tag>
                                </Tooltip>
                            );
                        } else {
                            var debt = record.amount - record.paidAmount;
                            statusTag = (
                                <Tooltip color='var(--main-red)' title={`Оплачено на сумму: ${toFinanceString(record.paidAmount, 2)} $`}>
                                    <Tag color='var(--main-red)' style={{ fontWeight: 600, fontSize: 14 }}>
                                        -{toFinanceString(debt, 2)} €
                                    </Tag>
                                </Tooltip>
                            );
                        }
                    }

                    return (
                        <Flex gap='small' align='center' justify='space-between'>
                            <div style={{ color: record.amount <= 0 ? 'red' : '' }}>{toFinanceString(record.amount, 2)} € </div>
                            <div>{statusTag}</div>
                        </Flex>
                    );
                },
            },
            {
                title: 'К оплате / ₽',
                width: 120,
                align: 'center',
                onCell: (record) => ({
                    colSpan: record.type == UserBalanceDetailType.Transaction ? 0 : 1,
                    style: {
                        fontWeight: 600,
                        background: record.groupId ? '#b4a7d6' : '',
                    },
                }),
                render: (_: any, record) => {
                    if (!record.amountRub || (record.groupId && record.id != record.groupId)) return;

                    return <div style={{ color: record.amountRub <= 0 ? 'red' : '' }}>{toFinanceString(record.amountRub, 2)} ₽</div>;
                },
            },
            {
                title: '',
                width: 120,
                onCell: (record) => ({
                    colSpan: record.type == UserBalanceDetailType.Transaction ? 0 : 1,
                    style: {
                        background: record.groupId ? '#b4a7d6' : '',
                    },
                }),
                render: (record) => {
                    if (record.type == UserBalanceDetailType.Transaction) {
                        return;
                    }

                    return (
                        <Flex style={{ width: '100%' }} align='center' justify='space-between'>
                            <Tooltip placement='top' title={`${toFinanceString(record.amountRub, 2)} ₽`}>
                                <Button
                                    style={{ width: 'fit-content', height: 'fit-content', padding: 0, fontWeight: 600 }}
                                    variant='link'
                                    color='blue'
                                >
                                    {record.groupFullCode}
                                </Button>
                            </Tooltip>

                            {!isViewMode && record.amountRub && (!record.groupId || record.id == record.groupId) && (
                                <Flex>
                                    <Tooltip placement='top' title='Изменить'>
                                        <Button
                                            style={{ width: 'fit-content', height: 'fit-content' }}
                                            variant='link'
                                            color='blue'
                                            icon={<EditFilled style={{ fontSize: 16 }} />}
                                            onClick={(e) => {
                                                setCurrentLine(record);
                                                setOpenRubleModal(true);
                                            }}
                                        />
                                    </Tooltip>

                                    {record.groupId && (
                                        <Tooltip placement='top' title='Удалить'>
                                            <Popconfirm
                                                title={`Удалить сумму ${toFinanceString(record.amountRub, 2)} ₽?`}
                                                onConfirm={() => {
                                                    record.groupId && onDeleteGroup(record.groupId);
                                                }}
                                            >
                                                <Button
                                                    style={{ width: 'fit-content', height: 'fit-content', marginLeft: 2 }}
                                                    variant='link'
                                                    color='blue'
                                                    icon={<DeleteFilled style={{ fontSize: 16 }} />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                    }}
                                                />
                                            </Popconfirm>
                                        </Tooltip>
                                    )}
                                </Flex>
                            )}
                        </Flex>
                    );
                },
            },
            {
                title: '',
                width: 32,
                onCell: (record) => ({
                    colSpan: record.type == UserBalanceDetailType.Transaction ? 0 : 1,
                    style: {
                        background: record.groupId ? '#b4a7d6' : '',
                    },
                }),
                render: (record) => {
                    if (record.type == UserBalanceDetailType.Transaction) return;

                    if (record.groupId && record.groupId != record.id) return;

                    if (isViewMode && !record.comment) return;

                    return (
                        <Comment
                            editMode={!isViewMode}
                            comment={record.comment}
                            onSave={(e) => {
                                onSaveComment(record.id, e, record.groupId);
                            }}
                        />
                    );
                },
            },
            {
                title: 'В оплату / €',
                width: 130,
                align: 'center',
                onCell: (record) => ({
                    style: {
                        background:
                            record.type == UserBalanceDetailType.Transaction ? TransactionTypeBackgroundColor(record.transactionType) : '',
                    },
                }),
                render: (_: any, record) => {
                    if (record.type != UserBalanceDetailType.Transaction) return;

                    return (
                        <Tooltip color={TransactionTypeColor(TransactionType.Transfer)} title='Расчетный счет "Транспорт" (€)'>
                            <div style={{ color: TransactionTypeColor(TransactionType.Transfer) }}>
                                <PlusOutlined />
                                <span style={{ fontWeight: 600, marginLeft: 5 }}>{toFinanceString(record.amount, 2)} €</span>
                            </div>
                        </Tooltip>
                    );
                },
            },
            {
                title: 'Баланс / €',
                width: 180,
                align: 'center',
                onCell: (record) => ({
                    style: {
                        fontWeight: 600,
                    },
                }),
                render: (_: any, record) => {
                    if (record.balanceAmount == undefined) return;

                    var icon = record.balanceAmount < 0 ? <MinusOutlined /> : record.balanceAmount > 0 ? <PlusOutlined /> : '';

                    var noSignBalanceAmount = record.balanceAmount < 0 ? -1 * record.balanceAmount : record.balanceAmount;
                    return (
                        <div style={{ color: record.balanceAmount < 0 ? 'var(--main-red)' : 'var(--main-green)' }}>
                            {icon}
                            <span style={{ fontWeight: 600, marginLeft: 5 }}>{toFinanceString(noSignBalanceAmount, 2)} €</span>
                        </div>
                    );
                },
            },
            {},
        ];

        return (
            <Table
                rowKey='id'
                size='small'
                ref={tableRef}
                columns={columns}
                dataSource={lines}
                pagination={false}
                virtual
                rowSelection={{
                    selectedRowKeys: selectedIds,
                    onChange: onSelectChange,
                    columnWidth: 32,
                    getCheckboxProps: (record) => {
                        return {
                            style: {
                                display: record.type == UserBalanceDetailType.Transaction ? 'none' : '',
                            },
                        };
                    },
                }}
                onRow={(record) => {
                    return {
                        onClick: (event) => {
                            onSelectChange([record.id], [record]);
                        },
                    };
                }}
                loading={{
                    spinning: loading,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                scroll={{ y: height }}
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 253px - ${filterHeight}px + ${showFilter ? 0 : 16}px)` }}>
            <FormHeader
                title={
                    <Flex gap='middle' align='center'>
                        <div>
                            Баланс {userBalanceType == UserBalanceType.White ? 'White' : ''} "{user ? user.login : ''}":
                        </div>
                        <div
                            style={{
                                marginLeft: 5,
                                fontSize: 24,
                                fontWeight: 600,
                                color: currentBalance < 0 ? 'var(--main-red)' : '#6a329f',
                            }}
                        >
                            <Flex align='center'>
                                <div style={{ marginTop: 2, fontSize: 16 }}>
                                    {currentBalance < 0 ? <MinusOutlined /> : currentBalance > 0 ? <PlusOutlined /> : <></>}
                                </div>
                                <div style={{ marginLeft: 5 }}>
                                    {toFinanceString(currentBalance < 0 ? -1 * currentBalance : currentBalance, 2)}
                                </div>
                                <div style={{ marginLeft: 5 }}>{currencySign(Currency.Eur)}</div>
                            </Flex>
                        </div>
                    </Flex>
                }
                container={
                    <Flex gap='large' align='center' justify='space-between'>
                        {cargoAccount && (
                            <Account
                                tooltipPlacement='top'
                                type={cargoAccount.type}
                                userBalanceType={cargoAccount.userBalanceType}
                                currency={cargoAccount.currency}
                                amount={cargoAccount.amount}
                                size='middle'
                            />
                        )}
                    </Flex>
                }
            />

            {renderToolbar()}
            {showFilter && renderFilter()}
            {renderTable()}

            {user && openDepositingModal && (
                <DepositingModal
                    userId={user.id || ''}
                    userLogin={user.login || ''}
                    userBalanceType={userBalanceType}
                    onCancel={() => setOpenDepositingModal(false)}
                    onSave={() => {
                        setOpenDepositingModal(false);
                        setRefreshRequired(true);
                    }}
                    api={api}
                    serverFetch={serverFetch}
                />
            )}

            {user && openTransferAccountsModal && (
                <TransferAccountsModal
                    userId={user.id || ''}
                    userLogin={user.login || ''}
                    accounts={userAccounts}
                    userBalanceType={userBalanceType}
                    onCancel={() => setOpenTransferAccountsModal(false)}
                    onSave={() => {
                        setRefreshRequired(true);
                        setOpenTransferAccountsModal(false);
                    }}
                    api={api}
                    serverFetch={serverFetch}
                />
            )}

            {user && user.id && openTransactions && (
                <Drawer
                    closable
                    destroyOnHidden
                    title={`Операции "${user.login}"`}
                    placement='right'
                    open={true}
                    onClose={() => setOpenTransactions(false)}
                    size={1200}
                >
                    <Transactions
                        userId={user.id}
                        useTagRequired={useTagRequired}
                        tag={tag}
                        isViewMode={false}
                        currency={Currency.Eur}
                        userBalanceType={userBalanceType}
                        onRefresh={() => setRefreshRequired(true)}
                        serverFetch={serverFetch}
                    />
                </Drawer>
            )}

            {user && openBillModal && (
                <BillModal
                    userId={user.id || ''}
                    userLogin={user.login || ''}
                    billId={currentLine?.referenceId}
                    currency={Currency.Eur}
                    api={api}
                    onCancel={() => setOpenBillModal(false)}
                    onSave={(value: IBill) => onSaveBill(value)}
                    serverFetch={serverFetch}
                />
            )}

            {user && openRefundModal && (
                <RefundModal
                    userId={userId}
                    userLogin={user?.login || ''}
                    accounts={userAccounts}
                    onCancel={() => setOpenRefundModal(false)}
                    onSave={() => {
                        setOpenRefundModal(false);
                        setRefreshRequired(true);
                    }}
                    api={api}
                    serverFetch={serverFetch}
                />
            )}

            {openBill && (
                <Bill
                    bills={bills}
                    onClose={() => {
                        setBills([]);
                        setOpenBill(false);
                    }}
                />
            )}

            {openRubleModal && (
                <RubleBillModal
                    entity={currentLine}
                    lineIds={selectedIds as Array<string>}
                    api={api}
                    serverFetch={serverFetch}
                    onCancel={() => setOpenRubleModal(false)}
                    onSave={() => {
                        setRefreshRequired(true);
                        setOpenRubleModal(false);
                    }}
                />
            )}

            <Modal
                width={400}
                title='Причина удаления счета'
                open={openDeleteModal}
                okText='ОК'
                closable={false}
                onOk={() => {
                    deletelForm.submit();
                }}
                onCancel={() => {
                    deletelForm.resetFields();
                    setOpenDeleteModal(false);
                }}
            >
                <Form colon={false} wrapperCol={{ span: 24 }} form={deletelForm} onFinish={onDelete}>
                    <Form.Item name='reason' required rules={[{ required: true, message: 'Укажите причину удаления' }]}>
                        <TextArea rows={6} />
                    </Form.Item>
                </Form>
            </Modal>

            {notificationContextHolder}
            {modalContextHolder}
        </div>
    );
};

export default BalanceDetailsControl;
